import {
  useCallback,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import { preventLeadingWhitespace } from '@/utils/helperFunctions';
import clsx from 'clsx';
import { getClubResourceValidationSchema } from '@/utils/validations';
import { Modal } from '@/components';
import PriceInput from '@/components/inputControls/PriceInput';
import { KeenIcon } from '@/components/keenicons';
import CircularProgress from '@mui/material/CircularProgress';
import {
  IClubResourceData,
  PriceUnit,
  RecurrencePattern,
  ResourceType,
  SaunaTimeWindow,
  SaunaWeeklyWindows,
  SaunaSpecialDateWindow,
} from '@/types/club-resource.types';
import { useGetClubResourceByIdQuery } from '@/services/ClubResourceService';
import { SeasonalDatePicker } from './SeasonalDatePicker';
import * as Yup from 'yup';
import { useLanguage, I18N_LANGUAGES } from '@/i18n';
import { useIntl } from 'react-intl';
import ClubResourcePreviewModal from './ClubResourcePreviewModal';
import { useTranslateClubResourceContentMutation } from '@/services/ClubResourceService';
import NumberInput from '@/components/inputControls/NumberInput';

interface ClubResourceFormModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit' | 'view';
  resourceId?: string;
  formData: Partial<IClubResourceData>;
  setFormData: Dispatch<SetStateAction<Partial<IClubResourceData>>>;
  errors: { [key: string]: string };
  setErrors: Dispatch<SetStateAction<{ [key: string]: string }>>;
  onSubmit: (formData: Partial<IClubResourceData>) => Promise<void>;
  isSubmitting?: boolean;
  validateField?: (name: string, value: unknown) => Promise<void>;
  className?: string;
}

const ClubResourceFormModal = ({
  mode,
  resourceId,
  formData: initialFormData,
  setFormData,
  errors,
  setErrors,
  onSubmit: onSubmitProp,
  isSubmitting,
  open,
  onClose,
  className,
  ...props
}: ClubResourceFormModalProps) => {
  const { currentLanguage, changeLanguage } = useLanguage();
  const intl = useIntl();
  const [localFormData, setLocalFormData] =
    useState<Partial<IClubResourceData>>(initialFormData);
  const [slotLengthType, setSlotLengthType] = useState<'whole_day' | 'custom'>(
    'custom'
  );
  const [customHours, setCustomHours] = useState<string>('');
  const [customMinutes, setCustomMinutes] = useState<string>('');
  const [slotUnit, setSlotUnit] = useState<'hours' | 'minutes'>('hours');

  // Sauna-specific state
  const [weeklyWindows, setWeeklyWindows] = useState<SaunaWeeklyWindows>({});
  const [specialDateWindows, setSpecialDateWindows] = useState<
    SaunaSpecialDateWindow[]
  >([]);
  const [closedDates, setClosedDates] = useState<string[]>([]);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [translations, setTranslations] = useState<
    Record<
      string,
      {
        description?: string;
      }
    >
  >({});
  const [translateClubResourceContent] =
    useTranslateClubResourceContentMutation();

  const localFormDataRef = useRef(localFormData);
  useEffect(() => {
    localFormDataRef.current = localFormData;
  }, [localFormData]);

  const errorsRef = useRef(errors);
  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);

  // Derived flags for sauna recurrence requirements
  const isSauna = localFormData.resource_type === ResourceType.SAUNA_BOOKING;
  const weeklyWindowsRequired =
    isSauna &&
    (localFormData.recurrence_pattern === RecurrencePattern.DAILY ||
      localFormData.recurrence_pattern === RecurrencePattern.WEEKLY);

  // Define field validator BEFORE effects that reference it
  const runFieldValidation = useCallback(
    async (name: keyof IClubResourceData | string, value: unknown) => {
      try {
        await getClubResourceValidationSchema(intl).validateAt(
          name,
          { ...localFormData, [name]: value },
          {
            abortEarly: false,
            context: { isEditMode: mode === 'edit' },
          }
        );
        setErrors((prev) => ({
          ...prev,
          [name]: '',
        }));
      } catch (err) {
        const validationError = err as Yup.ValidationError;
        if (name === 'seasonal_dates') {
          setErrors((prev) => {
            const next = { ...prev } as Record<string, string>;
            const seasons = ['spring', 'summer', 'autumn', 'winter'] as const;
            // Clear previous seasonal field errors
            seasons.forEach((s) => {
              delete next[`${s}_start`];
              delete next[`${s}_end`];
              delete next[`${s}_start_date`];
              delete next[`${s}_end_date`];
              delete next[`seasonal_dates.${s}`];
            });
            // Map inner errors to flat keys consumable by SeasonalDatePicker
            if (validationError.inner && validationError.inner.length) {
              validationError.inner.forEach((ie) => {
                const path = ie.path || '';
                const m = path.match(
                  /^seasonal_dates\.(spring|summer|autumn|winter)(?:\[\d+\])?\.(start_date|end_date)$/
                );
                if (m) {
                  const season = m[1];
                  const field = m[2];
                  const shortKey = `${season}_${field === 'start_date' ? 'start' : 'end'}`;
                  const rawMessage = ie.message || validationError.message;
                  next[shortKey] = rawMessage || '';
                } else if (path === 'seasonal_dates') {
                  // Overall seasonal error (e.g., no seasons defined) – display under Spring
                  const rawMessage = ie.message || validationError.message;
                  next['seasonal_dates.spring'] = rawMessage || '';
                }
              });
            } else {
              next['seasonal_dates.spring'] = validationError.message || '';
            }
            return next;
          });
        } else {
          const message = validationError.message;
          setErrors((prev) => ({
            ...prev,
            [name]: message,
          }));
        }
      }
    },
    [localFormData, setErrors, mode, intl]
  );

  // Validate sauna-specific structures when they change so errors show inline
  useEffect(() => {
    if (!isSauna) return;
    runFieldValidation('weekly_windows', weeklyWindows);
  }, [
    isSauna,
    weeklyWindows,
    localFormData.recurrence_pattern,
    runFieldValidation,
  ]);

  useEffect(() => {
    if (!isSauna) return;
    runFieldValidation('special_date_windows', specialDateWindows);
    // Note: validation for time windows inside each special date already handled in schema
  }, [isSauna, specialDateWindows, runFieldValidation]);

  useEffect(() => {
    if (!isSauna) return;
    runFieldValidation('closed_dates', closedDates);
  }, [isSauna, closedDates, runFieldValidation]);

  const nameInputRef = useRef<HTMLInputElement>(null);

  // Precompute today's date and the sauna-specific max selectable date based on max booking period
  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);
  const saunaMaxDate = useMemo(() => {
    if (localFormData.resource_type !== ResourceType.SAUNA_BOOKING)
      return undefined;
    const days = localFormData.max_bookings_period_days;
    if (!days || days <= 0) return undefined;
    const date = new Date();
    // Allow selection up to the maximum booking period from today (inclusive)
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  }, [localFormData.resource_type, localFormData.max_bookings_period_days]);

  // Update local form data when prop changes
  useEffect(() => {
    const updatedFormData = {
      ...initialFormData,
      seasonal_dates: initialFormData.seasonal_dates || {
        spring: { start_date: '', end_date: '' },
        summer: { start_date: '', end_date: '' },
        autumn: { start_date: '', end_date: '' },
        winter: { start_date: '', end_date: '' },
      },
    };
    setLocalFormData(updatedFormData);
    if (initialFormData.translations) {
      setTranslations(initialFormData.translations);
    }

    // Initialize sauna-specific fields
    if (initialFormData.resource_type === ResourceType.SAUNA_BOOKING) {
      setWeeklyWindows(initialFormData.weekly_windows || {});
      setSpecialDateWindows(initialFormData.special_date_windows || []);
      setClosedDates(initialFormData.closed_dates || []);
    }

    // Parse slot_length to determine type and custom duration (hours/minutes)
    if (initialFormData.slot_length) {
      const slotLength = initialFormData.slot_length;
      if (slotLength === '24h') {
        setSlotLengthType('whole_day');
        setCustomHours('');
        setCustomMinutes('');
        setSlotUnit('hours');
      } else {
        setSlotLengthType('custom');
        if (typeof slotLength === 'string' && slotLength.endsWith('h')) {
          const hours = slotLength.replace('h', '');
          setCustomHours(hours);
          setCustomMinutes('');
          setSlotUnit('hours');
        } else if (typeof slotLength === 'string' && slotLength.endsWith('m')) {
          const minutes = slotLength.replace('m', '');
          setCustomMinutes(minutes);
          setCustomHours('');
          setSlotUnit('minutes');
        } else {
          setCustomHours('');
          setCustomMinutes('');
          setSlotUnit('hours');
        }
      }
    } else {
      setSlotLengthType('custom');
      setCustomHours('');
      setCustomMinutes('');
      setSlotUnit('hours');
    }
  }, [initialFormData]);

  const { data: resourceData, isLoading: isResourceLoading } =
    useGetClubResourceByIdQuery(resourceId || '', {
      skip: !resourceId || mode === 'create',
    });

  useEffect(() => {
    if (resourceData && (mode === 'view' || mode === 'edit')) {
      const updatedResourceData = {
        ...resourceData,
        seasonal_dates: resourceData.seasonal_dates || {
          spring: { start_date: '', end_date: '' },
          summer: { start_date: '', end_date: '' },
          autumn: { start_date: '', end_date: '' },
          winter: { start_date: '', end_date: '' },
        },
      };
      setLocalFormData(updatedResourceData);
      if (resourceData.translations) {
        setTranslations(resourceData.translations);
      }

      // Initialize sauna-specific fields from resource data
      if (resourceData.resource_type === ResourceType.SAUNA_BOOKING) {
        setWeeklyWindows(resourceData.weekly_windows || {});
        setSpecialDateWindows(resourceData.special_date_windows || []);
        setClosedDates(resourceData.closed_dates || []);
      }

      // Parse slot_length to determine type and custom duration (hours/minutes)
      if (resourceData.slot_length) {
        const slotLength = resourceData.slot_length;
        if (slotLength === '24h') {
          setSlotLengthType('whole_day');
          setCustomHours('');
          setCustomMinutes('');
          setSlotUnit('hours');
        } else {
          setSlotLengthType('custom');
          if (typeof slotLength === 'string' && slotLength.endsWith('h')) {
            const hours = slotLength.replace('h', '');
            setCustomHours(hours);
            setCustomMinutes('');
            setSlotUnit('hours');
          } else if (
            typeof slotLength === 'string' &&
            slotLength.endsWith('m')
          ) {
            const minutes = slotLength.replace('m', '');
            setCustomMinutes(minutes);
            setCustomHours('');
            setSlotUnit('minutes');
          } else {
            setCustomHours('');
            setCustomMinutes('');
            setSlotUnit('hours');
          }
        }
      }
    }
  }, [resourceData, mode]);

  // Focus the Name field when the modal opens (except in view mode)
  useEffect(() => {
    if (open && mode !== 'view') {
      const timer = setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, mode]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setLocalFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      runFieldValidation(name, value);
    },
    [runFieldValidation, setErrors]
  );

  const handleNumberInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
      const value =
        e.target.value === '' ? undefined : parseFloat(e.target.value);
      setLocalFormData((prev) => ({
        ...prev,
        [fieldName]: value,
      }));
      runFieldValidation(fieldName, value);
    },
    [runFieldValidation]
  );

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { name, value } = e.target;
      // Parse as integer for status and resource_type fields, keep string values for other enums
      const parsedValue =
        name === 'status' || name === 'resource_type'
          ? parseInt(value, 10)
          : value;

      setLocalFormData((prev) => {
        const newData = {
          ...prev,
          [name]: parsedValue,
        };

        // If resource_type is changed to Sauna Booking, auto-set price_unit based on current slot_length
        if (
          name === 'resource_type' &&
          parsedValue === ResourceType.SAUNA_BOOKING
        ) {
          if (prev.slot_length === '24h') {
            newData.price_unit = PriceUnit.PER_DAY;
          } else if (prev.slot_length && prev.slot_length !== '24h') {
            newData.price_unit = PriceUnit.PER_HOUR;
          }
        }

        // If recurrence pattern changes for Port Watch Shifts, tidy dependent fields
        if (
          name === 'recurrence_pattern' &&
          (prev.resource_type === ResourceType.PORT_WATCH_SHIFTS ||
            newData.resource_type === ResourceType.PORT_WATCH_SHIFTS)
        ) {
          if (parsedValue === RecurrencePattern.SINGLE) {
            newData.shift_end_date = '';
            setErrors((errs) => ({ ...errs, shift_end_date: '' }));
          }
          if (parsedValue === RecurrencePattern.DAILY) {
            setErrors((errs) => ({ ...errs, shift_end_date: '' }));
          }
          if (parsedValue === RecurrencePattern.SEASONAL) {
            newData.shift_start_date = '';
            newData.shift_end_date = '';
            setErrors((errs) => ({
              ...errs,
              shift_start_date: '',
              shift_end_date: '',
            }));
          }
        }

        return newData;
      });
      runFieldValidation(name, parsedValue);
    },
    [runFieldValidation]
  );

  const handleSlotLengthTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const type = e.target.value as 'whole_day' | 'custom';
      setSlotLengthType(type);

      if (type === 'whole_day') {
        // Automatically set to 24h for whole day and set price_unit to PER_DAY
        const slotLength = '24h';
        setCustomHours('');
        setCustomMinutes('');
        setSlotUnit('hours');
        setLocalFormData((prev) => ({
          ...prev,
          slot_length: slotLength,
          price_unit: PriceUnit.PER_DAY,
        }));
        runFieldValidation('slot_length', slotLength);
      } else {
        // Switching to custom duration
        setLocalFormData((prev) => {
          const isSaunaResource =
            prev.resource_type === ResourceType.SAUNA_BOOKING;
          const currentValue =
            slotUnit === 'hours' ? customHours : customMinutes;
          const suffix = slotUnit === 'hours' ? 'h' : 'm';
          const slotLength = isSaunaResource
            ? currentValue
              ? `${currentValue}${suffix}`
              : ''
            : customHours
              ? `${customHours}h`
              : '';

          return {
            ...prev,
            slot_length: slotLength,
            // For sauna + custom, we let user choose price_unit (Per Hour / Per Slot),
            // so do not force a unit here beyond preserving existing value.
            ...(isSaunaResource
              ? { price_unit: prev.price_unit || PriceUnit.PER_HOUR }
              : { price_unit: PriceUnit.PER_HOUR }),
          };
        });
        const validationValue =
          slotUnit === 'hours' ? customHours : customMinutes;
        const slotLengthForValidation = validationValue
          ? `${validationValue}${slotUnit === 'hours' ? 'h' : 'm'}`
          : '';
        runFieldValidation('slot_length', slotLengthForValidation);
      }
    },
    [customHours, customMinutes, slotUnit, runFieldValidation]
  );

  const handleCustomHoursChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      // Only allow positive integers
      if (value === '' || /^\d+$/.test(value)) {
        setCustomHours(value);
        const slotLength = value ? `${value}h` : '';
        setLocalFormData((prev) => {
          const isSaunaResource =
            prev.resource_type === ResourceType.SAUNA_BOOKING;
          return {
            ...prev,
            slot_length: slotLength,
            // For sauna + custom, price unit is user controlled; do not override.
            ...(isSaunaResource ? {} : { price_unit: PriceUnit.PER_HOUR }),
          };
        });
        runFieldValidation('slot_length', slotLength);
      }
    },
    [runFieldValidation]
  );

  const handleCustomMinutesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      // Only allow positive integers
      if (value === '' || /^\d+$/.test(value)) {
        setCustomMinutes(value);
        const slotLength = value ? `${value}m` : '';
        setLocalFormData((prev) => {
          const isSaunaResource =
            prev.resource_type === ResourceType.SAUNA_BOOKING;
          return {
            ...prev,
            slot_length: slotLength,
            // For sauna + custom, price unit is user controlled; do not override.
            ...(isSaunaResource ? {} : { price_unit: PriceUnit.PER_HOUR }),
          };
        });
        runFieldValidation('slot_length', slotLength);
      }
    },
    [runFieldValidation]
  );

  const handleSlotUnitChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const unit = e.target.value as 'hours' | 'minutes';
      setSlotUnit(unit);

      const currentValue = unit === 'hours' ? customHours : customMinutes;
      const suffix = unit === 'hours' ? 'h' : 'm';
      const slotLength = currentValue ? `${currentValue}${suffix}` : '';

      setLocalFormData((prev) => ({
        ...prev,
        slot_length: slotLength,
      }));
      runFieldValidation('slot_length', slotLength);
    },
    [customHours, customMinutes, runFieldValidation]
  );

  // Helper: parse slot_length to minutes (e.g., "1h" => 60). Defaults to 60 if not set/invalid.
  const getSlotLengthMinutes = useCallback(() => {
    const slot = localFormData.slot_length;
    if (!slot) return 60;
    if (slot === '24h') return 24 * 60;
    if (typeof slot === 'string' && slot.endsWith('h')) {
      const hours = parseInt(slot.replace('h', ''), 10);
      return Number.isFinite(hours) && hours > 0 ? hours * 60 : 60;
    }
    if (typeof slot === 'string' && slot.endsWith('m')) {
      const minutes = parseInt(slot.replace('m', ''), 10);
      return Number.isFinite(minutes) && minutes > 0 ? minutes : 60;
    }
    return 60;
  }, [localFormData.slot_length]);

  // Helper: compute end time based on start time and slot length, clamped to same day (23:59)
  const computeEndTime = useCallback(
    (start: string) => {
      if (!start) return '';
      const [hs, ms] = start.split(':');
      const startMin = parseInt(hs || '0', 10) * 60 + parseInt(ms || '0', 10);
      const total = Math.min(startMin + getSlotLengthMinutes(), 24 * 60 - 1);
      const eh = Math.floor(total / 60);
      const em = total % 60;
      return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
    },
    [getSlotLengthMinutes]
  );

  const handleSeasonalDateChange = useCallback(
    (seasonalDates: IClubResourceData['seasonal_dates']) => {
      setLocalFormData((prev) => ({
        ...prev,
        seasonal_dates: seasonalDates,
      }));
      // Do not validate on every change to avoid irritating UX; clear any existing seasonal errors while editing
      setErrors((prev) => {
        const next = { ...prev } as Record<string, string>;
        const seasons = ['spring', 'summer', 'autumn', 'winter'] as const;
        seasons.forEach((s) => {
          delete next[`${s}_start`];
          delete next[`${s}_end`];
          delete next[`${s}_start_date`];
          delete next[`${s}_end_date`];
          delete next[`seasonal_dates.${s}`];
        });
        delete next['seasonal_dates']; // clear any root error
        return next;
      });
    },
    [setErrors]
  );

  // Sauna-specific handlers
  const handleAddWeeklyWindow = useCallback(
    (day: keyof SaunaWeeklyWindows) => {
      const defaultStart = '09:00';
      const newWindow: SaunaTimeWindow = {
        start_time: defaultStart,
        end_time: computeEndTime(defaultStart) || '10:00',
      };
      setWeeklyWindows((prev) => ({
        ...prev,
        [day]: [...(prev[day] || []), newWindow],
      }));
    },
    [computeEndTime]
  );

  const handleUpdateWeeklyWindow = useCallback(
    (
      day: keyof SaunaWeeklyWindows,
      index: number,
      timeWindow: SaunaTimeWindow
    ) => {
      setWeeklyWindows((prev) => ({
        ...prev,
        [day]:
          prev[day]?.map((w, i) => {
            if (i !== index) return w;
            const updated: SaunaTimeWindow = { ...timeWindow };
            // Auto-calc end_time from start_time based on slot_length
            updated.end_time =
              computeEndTime(updated.start_time) || updated.end_time;
            return updated;
          }) || [],
      }));
    },
    [computeEndTime]
  );

  const handleRemoveWeeklyWindow = useCallback(
    (day: keyof SaunaWeeklyWindows, index: number) => {
      setWeeklyWindows((prev) => ({
        ...prev,
        [day]: prev[day]?.filter((_, i) => i !== index) || [],
      }));
    },
    []
  );

  const handleAddSpecialDateWindow = useCallback(() => {
    const defaultStart = new Date().toISOString().split('T')[0];
    const defaultTime = '09:00';
    const newSpecialDate: SaunaSpecialDateWindow = {
      date: defaultStart,
      windows: [
        {
          start_time: defaultTime,
          end_time: computeEndTime(defaultTime) || '10:00',
        },
      ],
    };
    setSpecialDateWindows((prev) => [...prev, newSpecialDate]);
  }, [computeEndTime]);

  const handleUpdateSpecialDateWindow = useCallback(
    (index: number, specialDate: SaunaSpecialDateWindow) => {
      setSpecialDateWindows((prev) =>
        prev.map((sd, i) => {
          if (i !== index) return sd;
          // Recalculate all window end_times based on their start_time
          const recalculated: SaunaSpecialDateWindow = {
            ...specialDate,
            windows: (specialDate.windows || []).map((w) => ({
              ...w,
              end_time: computeEndTime(w.start_time) || w.end_time,
            })),
          };
          return recalculated;
        })
      );
    },
    [computeEndTime]
  );

  // Recompute end_time for all sauna windows when slot_length changes
  useEffect(() => {
    if (localFormData.resource_type !== ResourceType.SAUNA_BOOKING) return;
    setWeeklyWindows((prev) => {
      const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
      const updated: SaunaWeeklyWindows = {};
      days.forEach((d) => {
        const arr = prev[d] || [];
        updated[d] = arr.map((w) => ({
          ...w,
          end_time: computeEndTime(w.start_time) || w.end_time,
        }));
      });
      return updated;
    });
    setSpecialDateWindows((prev) =>
      prev.map((sd) => ({
        ...sd,
        windows: sd.windows.map((w) => ({
          ...w,
          end_time: computeEndTime(w.start_time) || w.end_time,
        })),
      }))
    );
  }, [localFormData.slot_length, localFormData.resource_type, computeEndTime]);

  const handleRemoveSpecialDateWindow = useCallback((index: number) => {
    setSpecialDateWindows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddClosedDate = useCallback(() => {
    const newDate = new Date().toISOString().split('T')[0];
    if (!closedDates.includes(newDate)) {
      setClosedDates((prev) => [...prev, newDate]);
    }
  }, [closedDates]);

  const handleUpdateClosedDate = useCallback((index: number, date: string) => {
    setClosedDates((prev) => prev.map((d, i) => (i === index ? date : d)));
  }, []);

  const handleRemoveClosedDate = useCallback((index: number) => {
    setClosedDates((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Update parent component with latest form data before submission
      // Ensure translations are available; generate if missing
      let translationsToUse = translations;
      if (
        (!translationsToUse || Object.keys(translationsToUse).length === 0) &&
        localFormData.description
      ) {
        try {
          const payload = {
            description: localFormData.description,
            source_language: currentLanguage.code || 'en',
            resource_id: localFormData.id
              ? String(localFormData.id)
              : undefined,
          };
          const response = await translateClubResourceContent(payload).unwrap();
          if (response && response.translations) {
            translationsToUse = response.translations as typeof translations;
            setTranslations(translationsToUse);
          }
        } catch (err) {
          // non-blocking: proceed without translations
          console.error('Translation failed', err);
        }
      }

      const updatedFormData = {
        ...localFormData,
        // Include sauna-specific fields if it's a sauna resource
        ...(localFormData.resource_type === ResourceType.SAUNA_BOOKING && {
          weekly_windows: weeklyWindows,
          special_date_windows: specialDateWindows,
          closed_dates: closedDates,
        }),
        translations: translationsToUse,
      };

      setFormData(updatedFormData);
      await onSubmitProp(updatedFormData);
    },
    [
      localFormData,
      weeklyWindows,
      specialDateWindows,
      closedDates,
      translations,
      onSubmitProp,
      setFormData,
      currentLanguage.code,
      translateClubResourceContent,
    ]
  );

  useEffect(() => {
    if (!open) return;
    if (!Object.keys(errorsRef.current).length) return;

    (async () => {
      try {
        await getClubResourceValidationSchema(intl).validate(
          localFormDataRef.current,
          {
            abortEarly: false,
            context: { isEditMode: mode === 'edit' },
          }
        );
        setErrors({});
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const validationError = err;
          const next: Record<string, string> = {};

          validationError.inner.forEach((ie) => {
            const path = ie.path || '';
            const message = ie.message || validationError.message || '';

            const match = path.match(
              /^seasonal_dates\.(spring|summer|autumn|winter)(?:\[\d+\])?\.(start_date|end_date)$/
            );
            if (match) {
              const season = match[1];
              const field = match[2];
              const shortKey = `${season}_${
                field === 'start_date' ? 'start' : 'end'
              }`;
              next[shortKey] = message;
            } else if (path === 'seasonal_dates') {
              next['seasonal_dates.spring'] = message;
            } else if (path) {
              next[path] = message;
            }
          });

          setErrors(next);
        }
      }
    })();
  }, [currentLanguage.code, intl.locale, open, mode, setErrors]);

  const isReadOnly = mode === 'view';

  if (isResourceLoading) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="club-resource-loading-title"
        className="!flex items-center justify-center"
      >
        <div className="!flex justify-center p-8">
          <h2 id="club-resource-loading-title" className="sr-only">
            Loading club resource
          </h2>
          <CircularProgress aria-label="Loading club resource" />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="club-resource-modal-title"
      className={clsx('!flex items-center justify-center', className)}
      {...props}
    >
      <div className="bg-gray-100 p-8 w-full max-w-2xl mx-4 rounded-xl shadow-2xl flex flex-col responsive-form club-resource-modal">
        <div className="flex justify-between items-center mb-6">
          <h1
            id="club-resource-modal-title"
            className="text-2xl font-semibold text-gray-800"
          >
            {mode === 'create'
              ? intl.formatMessage({
                  id: 'CLUB_RESOURCE.FORM.CREATE.TITLE',
                })
              : mode === 'edit'
                ? intl.formatMessage({
                    id: 'CLUB_RESOURCE.FORM.EDIT.TITLE',
                  })
                : intl.formatMessage({
                    id: 'CLUB_RESOURCE.FORM.VIEW.TITLE',
                  })}
          </h1>
          <div className="relative">
            <select
              id="club-resource-language-select"
              className="select select-bordered pl-8 pr-8 h-10 leading-normal"
              value={currentLanguage.code}
              onChange={(e) => {
                const selectedLang = I18N_LANGUAGES.find(
                  (lang: { code: string; label: string }) =>
                    lang.code === e.target.value
                );
                if (selectedLang) {
                  changeLanguage(selectedLang);
                }
              }}
              aria-label={intl.formatMessage({
                id: 'CLUB_RESOURCE.FORM.SELECT_LANGUAGE',
              })}
            >
              {I18N_LANGUAGES.map((lang: { code: string; label: string }) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
            <img
              src="/media/flags/globe.svg"
              alt="Globe"
              className="absolute dark:brightness-0 dark:invert contrast:brightness-0 dark:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
              aria-hidden="true"
            />
          </div>
        </div>
        <div id="club-resource-modal-description" className="sr-only">
          {mode === 'create'
            ? intl.formatMessage({
                id: 'CLUB_RESOURCE.FORM.CREATE.DESCRIPTION',
              })
            : mode === 'edit'
              ? intl.formatMessage({
                  id: 'CLUB_RESOURCE.FORM.EDIT.DESCRIPTION',
                })
              : intl.formatMessage({
                  id: 'CLUB_RESOURCE.FORM.VIEW.DESCRIPTION',
                })}
        </div>
        <div
          tabIndex={0}
          className="flex-1 overflow-y-auto max-h-[70vh] pr-2 custom-scrollbar"
        >
          <form onSubmit={onSubmit}>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-6">
              {/* Name */}
              <div className="col-span-1 ">
                <label
                  htmlFor="name-input"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'COMMON.NAME' })}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  ref={nameInputRef}
                  id="name-input"
                  name="name"
                  type="text"
                  autoComplete="off"
                  className={clsx(
                    'input input-bordered w-full',
                    errors.name && 'border-red-500 focus:ring-red-500'
                  )}
                  value={localFormData.name || ''}
                  onChange={handleInputChange}
                  onKeyDown={preventLeadingWhitespace}
                  disabled={isReadOnly}
                  placeholder={
                    localFormData.resource_type === ResourceType.SAUNA_BOOKING
                      ? intl.formatMessage({
                          id: 'CLUB_RESOURCE.FORM.NAME.PLACEHOLDER_SAUNA',
                        })
                      : intl.formatMessage({
                          id: 'CLUB_RESOURCE.FORM.NAME.PLACEHOLDER',
                        })
                  }
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                />
                {errors.name && (
                  <p id="name-error" className="text-red-500 text-sm mt-1">
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Resource Type */}
              <div className="col-span-1">
                <label
                  htmlFor="resource-type-select"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({
                    id: 'CLUB_RESOURCE.FORM.RESOURCE_TYPE',
                  })}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  id="resource-type-select"
                  name="resource_type"
                  className={clsx(
                    'select select-bordered w-full',
                    errors.resource_type && 'border-red-500 focus:ring-red-500'
                  )}
                  value={localFormData.resource_type || ''}
                  onChange={handleSelectChange}
                  disabled={isReadOnly}
                  aria-invalid={!!errors.resource_type}
                  aria-describedby={
                    errors.resource_type ? 'resource-type-error' : undefined
                  }
                >
                  <option value="">
                    {intl.formatMessage({
                      id: 'CLUB_RESOURCE.FORM.RESOURCE_TYPE.SELECT',
                    })}
                  </option>
                  <option value={ResourceType.SAUNA_BOOKING}>
                    {intl.formatMessage({
                      id: 'CLUB_RESOURCE.FORM.RESOURCE_TYPE.SAUNA',
                    })}
                  </option>
                  <option value={ResourceType.KEYS}>
                    {intl.formatMessage({
                      id: 'CLUB_RESOURCE.FORM.RESOURCE_TYPE.KEYS',
                    })}
                  </option>
                  <option value={ResourceType.PORT_WATCH_SHIFTS}>
                    {intl.formatMessage({
                      id: 'CLUB_RESOURCE.FORM.RESOURCE_TYPE.PORT_WATCH_SHIFTS',
                    })}
                  </option>
                </select>
                {errors.resource_type && (
                  <p
                    id="resource-type-error"
                    className="text-red-500 text-sm mt-1"
                  >
                    {errors.resource_type}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="col-span-1 md:col-span-2">
                <label
                  htmlFor="description-input"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'COMMON.DESCRIPTION' })}
                </label>
                <textarea
                  id="description-input"
                  name="description"
                  className={clsx(
                    'textarea textarea-bordered w-full',
                    errors.description && 'border-red-500 focus:ring-red-500'
                  )}
                  value={localFormData.description || ''}
                  onChange={handleInputChange}
                  onKeyDown={preventLeadingWhitespace}
                  disabled={isReadOnly}
                  placeholder={intl.formatMessage({
                    id: 'CLUB_RESOURCE.FORM.DESCRIPTION.PLACEHOLDER',
                  })}
                  rows={4}
                  aria-invalid={!!errors.description}
                  aria-describedby={
                    errors.description ? 'description-error' : undefined
                  }
                />
                {errors.description && (
                  <p
                    id="description-error"
                    className="text-red-500 text-sm mt-1"
                  >
                    {errors.description}
                  </p>
                )}
              </div>

              {!isReadOnly && localFormData.description && (
                <div className="col-span-1 md:col-span-2 mt-2 bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 border border-blue-200 dark:border-blue-800 contrast:border-blue-800 rounded-lg p-3 flex items-center justify-between gap-2 club-resource-preview-notice">
                  <div className="flex items-center">
                    <KeenIcon
                      type="outline"
                      icon="paper-clip"
                      className="w-5 h-5 text-blue-600 dark:text-blue-400 contrast:text-blue-400 mr-2"
                    />
                    <p className="text-blue-700 dark:text-blue-400 contrast:text-blue-200 text-sm club-resource-preview-notice-text">
                      {intl.formatMessage({
                        id: 'CLUB_RESOURCE.PREVIEW.NOTICE',
                      })}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsPreviewModalOpen(true)}
                    className="btn btn-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 club-resource-preview-notice-button"
                    aria-label={intl.formatMessage({
                      id: 'CLUB_RESOURCE.BUTTON.SHOW_PREVIEW',
                    })}
                    disabled={isSubmitting}
                  >
                    {intl.formatMessage({
                      id: 'CLUB_RESOURCE.BUTTON.SHOW_PREVIEW',
                    })}
                  </button>
                </div>
              )}

              {/* Slot Length - Hidden for Keys and Port Watch Shifts */}
              {localFormData.resource_type !== ResourceType.KEYS &&
                localFormData.resource_type !==
                  ResourceType.PORT_WATCH_SHIFTS && (
                  <div className="col-span-1">
                    <label
                      htmlFor="slot-length-type-select"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {intl.formatMessage({
                        id: 'CLUB_RESOURCE.FORM.SLOT_LENGTH',
                      })}
                    </label>
                    <select
                      id="slot-length-type-select"
                      name="slot_length_type"
                      className="select select-bordered w-full"
                      value={slotLengthType}
                      onChange={handleSlotLengthTypeChange}
                      disabled={isReadOnly}
                    >
                      <option value="custom">
                        {localFormData.resource_type ===
                        ResourceType.SAUNA_BOOKING
                          ? intl.formatMessage({
                              id: 'CLUB_RESOURCE.FORM.SLOT_LENGTH.CUSTOM_DURATION',
                            })
                          : intl.formatMessage({
                              id: 'CLUB_RESOURCE.FORM.SLOT_LENGTH.CUSTOM_HOURS',
                            })}
                      </option>
                      <option value="whole_day">
                        {intl.formatMessage({
                          id: 'CLUB_RESOURCE.FORM.SLOT_LENGTH.WHOLE_DAY',
                        })}
                      </option>
                    </select>
                  </div>
                )}

              {/* Quantity - Only for Keys and Annual Inspections */}
              {(localFormData.resource_type === ResourceType.KEYS ||
                localFormData.resource_type ===
                  ResourceType.ANNUAL_INSPECTIONS) && (
                <div className="col-span-1">
                  <label
                    htmlFor="quantity-input"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Quantity
                  </label>
                  <NumberInput
                    id="quantity-input"
                    name="quantity"
                    autoComplete="off"
                    className={clsx(
                      'input input-bordered w-full',
                      errors.quantity && 'border-red-500 focus:ring-red-500'
                    )}
                    value={
                      localFormData.quantity !== undefined
                        ? localFormData.quantity
                        : ''
                    }
                    onChange={(e) => handleNumberInputChange(e, 'quantity')}
                    disabled={isReadOnly}
                    placeholder={intl.formatMessage({
                      id: 'CLUB_RESOURCE.FORM.QUANTITY.PLACEHOLDER',
                    })}
                    min="1"
                    aria-invalid={!!errors.quantity}
                    aria-describedby={
                      errors.quantity ? 'quantity-error' : undefined
                    }
                  />
                  {errors.quantity && (
                    <p
                      id="quantity-error"
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.quantity}
                    </p>
                  )}
                </div>
              )}

              {/* Custom Duration Input - Conditional (Hidden for Keys and Port Watch Shifts) */}
              {slotLengthType === 'custom' &&
                localFormData.resource_type !== ResourceType.KEYS &&
                localFormData.resource_type !==
                  ResourceType.PORT_WATCH_SHIFTS && (
                  <>
                    {localFormData.resource_type ===
                    ResourceType.SAUNA_BOOKING ? (
                      <>
                        {/* Duration Unit Selection (Sauna only) */}
                        <div className="col-span-1">
                          <label
                            htmlFor="slot-unit-select"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            {intl.formatMessage({
                              id: 'CLUB_RESOURCE.FORM.DURATION_UNIT',
                            })}
                          </label>
                          <select
                            id="slot-unit-select"
                            name="slot_unit"
                            className="select select-bordered w-full"
                            value={slotUnit}
                            onChange={handleSlotUnitChange}
                            disabled={isReadOnly}
                          >
                            <option value="hours">
                              {intl.formatMessage({
                                id: 'CLUB_RESOURCE.FORM.DURATION_UNIT.HOURS',
                              })}
                            </option>
                            <option value="minutes">
                              {intl.formatMessage({
                                id: 'CLUB_RESOURCE.FORM.DURATION_UNIT.MINUTES',
                              })}
                            </option>
                          </select>
                        </div>

                        {/* Duration Value Input (Sauna only) */}
                        <div className="col-span-1">
                          <label
                            htmlFor="custom-duration-input"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            {slotUnit === 'hours'
                              ? intl.formatMessage({
                                  id: 'CLUB_RESOURCE.FORM.DURATION_VALUE.HOURS',
                                })
                              : intl.formatMessage({
                                  id: 'CLUB_RESOURCE.FORM.DURATION_VALUE.MINUTES',
                                })}
                          </label>
                          <NumberInput
                            id="custom-duration-input"
                            name="custom_duration"
                            autoComplete="off"
                            className={clsx(
                              'input input-bordered w-full',
                              errors.slot_length &&
                                'border-red-500 focus:ring-red-500'
                            )}
                            value={
                              slotUnit === 'hours' ? customHours : customMinutes
                            }
                            onChange={
                              slotUnit === 'hours'
                                ? handleCustomHoursChange
                                : handleCustomMinutesChange
                            }
                            disabled={isReadOnly}
                            placeholder={
                              slotUnit === 'hours'
                                ? intl.formatMessage({
                                    id: 'CLUB_RESOURCE.FORM.DURATION_VALUE.HOURS.PLACEHOLDER',
                                  })
                                : intl.formatMessage({
                                    id: 'CLUB_RESOURCE.FORM.DURATION_VALUE.MINUTES.PLACEHOLDER',
                                  })
                            }
                            min="1"
                            max={slotUnit === 'hours' ? '24' : '1440'}
                            aria-invalid={!!errors.slot_length}
                            aria-describedby={
                              errors.slot_length
                                ? 'custom-duration-error'
                                : undefined
                            }
                          />
                          {errors.slot_length && (
                            <p
                              id="custom-duration-error"
                              className="text-red-500 text-sm mt-1"
                            >
                              {errors.slot_length}
                            </p>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="col-span-1">
                        <label
                          htmlFor="custom-hours-input"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          {intl.formatMessage({
                            id: 'CLUB_RESOURCE.FORM.DURATION_VALUE.HOURS',
                          })}
                        </label>
                        <NumberInput
                          id="custom-hours-input"
                          name="custom_hours"
                          autoComplete="off"
                          className={clsx(
                            'input input-bordered w-full',
                            errors.slot_length &&
                              'border-red-500 focus:ring-red-500'
                          )}
                          value={customHours}
                          onChange={handleCustomHoursChange}
                          disabled={isReadOnly}
                          placeholder={intl.formatMessage({
                            id: 'CLUB_RESOURCE.FORM.CUSTOM_HOURS.PLACEHOLDER',
                          })}
                          min="1"
                          max="24"
                          aria-invalid={!!errors.slot_length}
                          aria-describedby={
                            errors.slot_length
                              ? 'custom-hours-error'
                              : undefined
                          }
                        />
                        {errors.slot_length && (
                          <p
                            id="custom-hours-error"
                            className="text-red-500 text-sm mt-1"
                          >
                            {errors.slot_length}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}

              {/* Recurrence Pattern - Only show here for non-PORT_WATCH_SHIFTS resources */}
              {localFormData.resource_type !==
                ResourceType.PORT_WATCH_SHIFTS && (
                <div className="col-span-1">
                  <label
                    htmlFor="recurrence-pattern-select"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {intl.formatMessage({
                      id: 'CLUB_RESOURCE.FORM.RECURRENCE_PATTERN',
                    })}{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="recurrence-pattern-select"
                    name="recurrence_pattern"
                    className={clsx(
                      'select select-bordered w-full',
                      errors.recurrence_pattern &&
                        'border-red-500 focus:ring-red-500'
                    )}
                    value={localFormData.recurrence_pattern || ''}
                    onChange={handleSelectChange}
                    disabled={isReadOnly}
                    aria-invalid={!!errors.recurrence_pattern}
                    aria-describedby={
                      errors.recurrence_pattern
                        ? 'recurrence-pattern-error'
                        : undefined
                    }
                  >
                    <option value="">
                      {intl.formatMessage({
                        id: 'CLUB_RESOURCE.FORM.RECURRENCE_PATTERN.SELECT',
                      })}
                    </option>
                    <option value={RecurrencePattern.SINGLE}>
                      {intl.formatMessage({
                        id: 'CLUB_RESOURCE.FORM.RECURRENCE_PATTERN.SINGLE',
                      })}
                    </option>
                    <option value={RecurrencePattern.DAILY}>
                      {intl.formatMessage({
                        id: 'CLUB_RESOURCE.FORM.RECURRENCE_PATTERN.DAILY',
                      })}
                    </option>
                    <option value={RecurrencePattern.WEEKLY}>
                      {intl.formatMessage({
                        id: 'CLUB_RESOURCE.FORM.RECURRENCE_PATTERN.WEEKLY',
                      })}
                    </option>
                    <option value={RecurrencePattern.SEASONAL}>
                      {intl.formatMessage({
                        id: 'CLUB_RESOURCE.FORM.RECURRENCE_PATTERN.SEASONAL',
                      })}
                    </option>
                  </select>
                  {errors.recurrence_pattern && (
                    <p
                      id="recurrence-pattern-error"
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.recurrence_pattern}
                    </p>
                  )}
                </div>
              )}

              {/* Max Bookings Period Days - Hidden for Port Watch Shifts */}
              {localFormData.resource_type !==
                ResourceType.PORT_WATCH_SHIFTS && (
                <div className="col-span-1">
                  <label
                    htmlFor="max-bookings-period-input"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {intl.formatMessage({
                      id: 'CLUB_RESOURCE.FORM.MAX_BOOKINGS_PERIOD_DAYS',
                    })}{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <NumberInput
                    id="max-bookings-period-input"
                    name="max_bookings_period_days"
                    autoComplete="off"
                    className={clsx(
                      'input input-bordered w-full',
                      errors.max_bookings_period_days &&
                        'border-red-500 focus:ring-red-500'
                    )}
                    value={
                      localFormData.max_bookings_period_days !== undefined
                        ? localFormData.max_bookings_period_days
                        : ''
                    }
                    onChange={(e) =>
                      handleNumberInputChange(e, 'max_bookings_period_days')
                    }
                    disabled={isReadOnly}
                    placeholder={intl.formatMessage({
                      id: 'CLUB_RESOURCE.FORM.MAX_DAYS.PLACEHOLDER',
                    })}
                    min="1"
                    max="365"
                    aria-invalid={!!errors.max_bookings_period_days}
                    aria-describedby={
                      errors.max_bookings_period_days
                        ? 'max-bookings-period-error'
                        : undefined
                    }
                  />
                  {errors.max_bookings_period_days && (
                    <p
                      id="max-bookings-period-error"
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.max_bookings_period_days}
                    </p>
                  )}
                </div>
              )}

              {/* Status */}
              <div className="col-span-1">
                <label
                  htmlFor="status-select"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'COMMON.STATUS' })}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  id="status-select"
                  name="status"
                  className={clsx(
                    'select select-bordered w-full',
                    errors.status && 'border-red-500 focus:ring-red-500'
                  )}
                  value={
                    localFormData.status !== undefined
                      ? localFormData.status
                      : 1
                  }
                  onChange={handleSelectChange}
                  disabled={isReadOnly}
                  aria-invalid={!!errors.status}
                  aria-describedby={errors.status ? 'status-error' : undefined}
                >
                  <option value={1}>
                    {intl.formatMessage({ id: 'COMMON.ACTIVE' })}
                  </option>
                  <option value={0}>
                    {intl.formatMessage({ id: 'COMMON.INACTIVE' })}
                  </option>
                </select>
                {errors.status && (
                  <p id="status-error" className="text-red-500 text-sm mt-1">
                    {errors.status}
                  </p>
                )}
              </div>

              {/* Allow Booking */}
              <div className="col-span-1">
                <label
                  htmlFor="allow-booking-checkbox"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({
                    id: 'CLUB_RESOURCE.FORM.ALLOW_BOOKING',
                  })}
                </label>
                <div className="flex items-center h-[40px]">
                  <input
                    id="allow-booking-checkbox"
                    name="allow_booking"
                    type="checkbox"
                    className="checkbox cursor-pointer"
                    checked={localFormData.allow_booking || false}
                    onChange={(e) =>
                      setLocalFormData((prev) => ({
                        ...prev,
                        allow_booking: e.target.checked,
                      }))
                    }
                    disabled={isReadOnly}
                    aria-invalid={!!errors.allow_booking}
                    aria-describedby={
                      errors.allow_booking ? 'allow-booking-error' : undefined
                    }
                  />
                  <span className="ml-2 text-sm text-gray-600 cursor-pointer">
                    {intl.formatMessage({
                      id: 'CLUB_RESOURCE.FORM.ALLOW_BOOKING.HELP_TEXT',
                    })}
                  </span>
                </div>
                {errors.allow_booking && (
                  <p
                    id="allow-booking-error"
                    className="text-red-500 text-sm mt-1"
                  >
                    {errors.allow_booking}
                  </p>
                )}
              </div>

              {/* Define Price - Hidden for Port Watch Shifts */}
              {localFormData.resource_type !==
                ResourceType.PORT_WATCH_SHIFTS && (
                <div className="col-span-1">
                  <label
                    htmlFor="define-price-checkbox"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {intl.formatMessage({
                      id: 'CLUB_RESOURCE.FORM.DEFINE_PRICE',
                    })}
                  </label>
                  <div className="flex items-center h-[40px]">
                    <input
                      id="define-price-checkbox"
                      name="define_price"
                      type="checkbox"
                      className="checkbox cursor-pointer"
                      checked={localFormData.define_price || false}
                      onChange={(e) =>
                        setLocalFormData((prev) => ({
                          ...prev,
                          define_price: e.target.checked,
                        }))
                      }
                      disabled={isReadOnly}
                      aria-invalid={!!errors.define_price}
                      aria-describedby={
                        errors.define_price ? 'define-price-error' : undefined
                      }
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {intl.formatMessage({
                        id: 'CLUB_RESOURCE.FORM.DEFINE_PRICE.HELP_TEXT',
                      })}
                    </span>
                  </div>
                  {errors.define_price && (
                    <p
                      id="define-price-error"
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.define_price}
                    </p>
                  )}
                </div>
              )}

              {/* Price - Conditional */}
              {localFormData.define_price && (
                <div className="col-span-1">
                  <label
                    htmlFor="price-input"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {intl.formatMessage({ id: 'COMMON.PRICE' })}
                  </label>
                  <PriceInput
                    id="price-input"
                    name="price"
                    value={localFormData.price ?? ''}
                    onChange={(val) => {
                      setLocalFormData((prev) => ({
                        ...prev,
                        price: val,
                      }));
                      runFieldValidation('price', val);
                    }}
                    disabled={isReadOnly}
                    inputClassName={clsx(
                      'input input-bordered w-full',
                      errors.price && 'border-red-500 focus:ring-red-500'
                    )}
                    error={errors.price}
                    placeholder={intl.formatMessage({
                      id: 'CLUB_RESOURCE.FORM.PRICE.PLACEHOLDER',
                    })}
                  />
                </div>
              )}

              {/* Price Unit - Conditional */}
              {localFormData.define_price && (
                <div className="col-span-1">
                  <label
                    htmlFor="price-unit-select"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Price Unit
                    {localFormData.resource_type ===
                      ResourceType.SAUNA_BOOKING && (
                      <span className="ml-2 text-xs text-gray-500">
                        {slotLengthType === 'custom'
                          ? '(Select Per Hour or Per Slot)'
                          : '(Auto-set based on Slot Length)'}
                      </span>
                    )}
                  </label>
                  <select
                    id="price-unit-select"
                    name="price_unit"
                    className={clsx(
                      'select select-bordered w-full',
                      errors.price_unit && 'border-red-500 focus:ring-red-500',
                      localFormData.resource_type ===
                        ResourceType.SAUNA_BOOKING &&
                        slotLengthType !== 'custom' &&
                        'bg-gray-100 cursor-not-allowed'
                    )}
                    value={localFormData.price_unit || ''}
                    onChange={handleSelectChange}
                    disabled={
                      isReadOnly ||
                      (localFormData.resource_type ===
                        ResourceType.SAUNA_BOOKING &&
                        slotLengthType !== 'custom')
                    }
                    aria-invalid={!!errors.price_unit}
                    aria-describedby={
                      errors.price_unit ? 'price-unit-error' : undefined
                    }
                  >
                    <option value="">Select unit</option>
                    {localFormData.resource_type ===
                      ResourceType.SAUNA_BOOKING &&
                    slotLengthType === 'custom' ? (
                      <>
                        {slotUnit === 'hours' && (
                          <option value={PriceUnit.PER_HOUR}>Per Hour</option>
                        )}
                        <option value={PriceUnit.PER_SLOT}>Per Slot</option>
                      </>
                    ) : localFormData.resource_type === ResourceType.KEYS ? (
                      <>
                        <option value={PriceUnit.PER_DAY}>Per Day</option>
                      </>
                    ) : (
                      <>
                        <option value={PriceUnit.PER_DAY}>Per Day</option>
                        <option value={PriceUnit.PER_HOUR}>Per Hour</option>
                      </>
                    )}
                  </select>
                  {errors.price_unit && (
                    <p
                      id="price-unit-error"
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.price_unit}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Port Watch Shifts Timing - Only for Port Watch Shifts */}
            {localFormData.resource_type === ResourceType.PORT_WATCH_SHIFTS && (
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/10 contrast:!bg-blue-900/10 rounded-lg border border-blue-200 dark:border-coal-500 contrast:!border-coal-500">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white contrast:text-white mb-4">
                  {intl.formatMessage({
                    id: 'CLUB_RESOURCE.SECTION.PORT_WATCH.TITLE',
                  })}
                </h2>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-6">
                  {/* Shift Start Time - Always shown first */}
                  <div className="col-span-1">
                    <label
                      htmlFor="shift-start-time-input"
                      className="block text-sm font-medium !text-gray-900 mb-2"
                    >
                      {intl.formatMessage({
                        id: 'CLUB_RESOURCE.FORM.SHIFT_START_TIME',
                      })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="shift-start-time-input"
                      name="shift_start_time"
                      type="time"
                      autoComplete="off"
                      className={clsx(
                        'input input-bordered w-full',
                        errors.shift_start_time &&
                          'border-red-500 focus:ring-red-500'
                      )}
                      value={localFormData.shift_start_time || ''}
                      onChange={handleInputChange}
                      disabled={
                        isReadOnly ||
                        (mode === 'edit' &&
                          localFormData.resource_type ===
                            ResourceType.PORT_WATCH_SHIFTS)
                      }
                      tabIndex={0}
                      onKeyDown={(e) => {
                        // Allow normal keyboard navigation for time inputs
                        if (
                          e.key === 'Tab' ||
                          e.key === 'Enter' ||
                          e.key === 'ArrowUp' ||
                          e.key === 'ArrowDown' ||
                          e.key === 'ArrowLeft' ||
                          e.key === 'ArrowRight' ||
                          e.key === 'Backspace' ||
                          e.key === 'Delete' ||
                          /^[0-9]$/.test(e.key)
                        ) {
                          return; // Allow these keys
                        }
                        e.preventDefault(); // Prevent other keys
                      }}
                      aria-invalid={!!errors.shift_start_time}
                      aria-describedby={
                        errors.shift_start_time
                          ? 'shift-start-time-error'
                          : undefined
                      }
                    />
                    {errors.shift_start_time && (
                      <p
                        id="shift-start-time-error"
                        className="text-red-500 text-sm mt-1"
                      >
                        {errors.shift_start_time}
                      </p>
                    )}
                  </div>

                  {/* Shift End Time - Always shown second */}
                  <div className="col-span-1">
                    <label
                      htmlFor="shift-end-time-input"
                      className="block text-sm font-medium !text-gray-900 mb-2"
                    >
                      {intl.formatMessage({
                        id: 'CLUB_RESOURCE.FORM.SHIFT_END_TIME',
                      })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="shift-end-time-input"
                      name="shift_end_time"
                      type="time"
                      autoComplete="off"
                      className={clsx(
                        'input input-bordered w-full',
                        errors.shift_end_time &&
                          'border-red-500 focus:ring-red-500'
                      )}
                      value={localFormData.shift_end_time || ''}
                      onChange={handleInputChange}
                      disabled={
                        isReadOnly ||
                        (mode === 'edit' &&
                          localFormData.resource_type ===
                            ResourceType.PORT_WATCH_SHIFTS)
                      }
                      tabIndex={0}
                      onKeyDown={(e) => {
                        // Allow normal keyboard navigation for time inputs
                        if (
                          e.key === 'Tab' ||
                          e.key === 'Enter' ||
                          e.key === 'ArrowUp' ||
                          e.key === 'ArrowDown' ||
                          e.key === 'ArrowLeft' ||
                          e.key === 'ArrowRight' ||
                          e.key === 'Backspace' ||
                          e.key === 'Delete' ||
                          /^[0-9]$/.test(e.key)
                        ) {
                          return; // Allow these keys
                        }
                        e.preventDefault(); // Prevent other keys
                      }}
                      aria-invalid={!!errors.shift_end_time}
                      aria-describedby={
                        errors.shift_end_time
                          ? 'shift-end-time-error'
                          : undefined
                      }
                    />
                    {errors.shift_end_time && (
                      <p
                        id="shift-end-time-error"
                        className="text-red-500 text-sm mt-1"
                      >
                        {errors.shift_end_time}
                      </p>
                    )}
                  </div>

                  {/* Single Date - Only shown when Single recurrence is selected */}
                  {localFormData.recurrence_pattern ===
                    RecurrencePattern.SINGLE && (
                    <div className="col-span-1">
                      <label
                        htmlFor="shift-single-date-input"
                        className="block text-sm font-medium !text-gray-900 mb-2"
                      >
                        {intl.formatMessage({
                          id: 'CLUB_RESOURCE.FORM.SHIFT_DATE',
                        })}{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="shift-single-date-input"
                        name="shift_start_date"
                        type="date"
                        autoComplete="off"
                        className={clsx(
                          'input input-bordered w-full',
                          errors.shift_start_date &&
                            'border-red-500 focus:ring-red-500'
                        )}
                        value={localFormData.shift_start_date || ''}
                        onChange={handleInputChange}
                        disabled={
                          isReadOnly ||
                          (mode === 'edit' &&
                            localFormData.resource_type ===
                              ResourceType.PORT_WATCH_SHIFTS)
                        }
                        min={new Date().toISOString().split('T')[0]}
                        readOnly={!isReadOnly ? false : true}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          // Only allow navigation keys, prevent typing
                          if (
                            e.key === 'Tab' ||
                            e.key === 'Enter' ||
                            e.key === 'ArrowUp' ||
                            e.key === 'ArrowDown' ||
                            e.key === 'ArrowLeft' ||
                            e.key === 'ArrowRight' ||
                            e.key === 'Escape'
                          ) {
                            return; // Allow navigation keys only
                          }
                          e.preventDefault(); // Prevent all other keys including typing
                        }}
                        aria-invalid={!!errors.shift_start_date}
                        aria-describedby={
                          errors.shift_start_date
                            ? 'shift-single-date-error'
                            : undefined
                        }
                      />
                      {errors.shift_start_date && (
                        <p
                          id="shift-single-date-error"
                          className="text-red-500 text-sm mt-1"
                        >
                          {errors.shift_start_date}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Shift Start Date - Only shown when Daily recurrence is selected */}
                  {localFormData.recurrence_pattern ===
                    RecurrencePattern.DAILY && (
                    <div className="col-span-1">
                      <label
                        htmlFor="shift-start-date-input"
                        className="block text-sm font-medium !text-gray-900 mb-2"
                      >
                        {intl.formatMessage({
                          id: 'CLUB_RESOURCE.FORM.SHIFT_START_DATE',
                        })}{' '}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="shift-start-date-input"
                        name="shift_start_date"
                        type="date"
                        autoComplete="off"
                        className={clsx(
                          'input input-bordered w-full',
                          errors.shift_start_date &&
                            'border-red-500 focus:ring-red-500'
                        )}
                        value={localFormData.shift_start_date || ''}
                        onChange={handleInputChange}
                        disabled={
                          isReadOnly ||
                          (mode === 'edit' &&
                            localFormData.resource_type ===
                              ResourceType.PORT_WATCH_SHIFTS)
                        }
                        min={new Date().toISOString().split('T')[0]}
                        readOnly={!isReadOnly ? false : true}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          // Only allow navigation keys, prevent typing
                          if (
                            e.key === 'Tab' ||
                            e.key === 'Enter' ||
                            e.key === 'ArrowUp' ||
                            e.key === 'ArrowDown' ||
                            e.key === 'ArrowLeft' ||
                            e.key === 'ArrowRight' ||
                            e.key === 'Escape'
                          ) {
                            return; // Allow navigation keys only
                          }
                          e.preventDefault(); // Prevent all other keys including typing
                        }}
                        aria-invalid={!!errors.shift_start_date}
                        aria-describedby={
                          errors.shift_start_date
                            ? 'shift-start-date-error'
                            : undefined
                        }
                      />
                      {errors.shift_start_date && (
                        <p
                          id="shift-start-date-error"
                          className="text-red-500 text-sm mt-1"
                        >
                          {errors.shift_start_date}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Shift End Date - Only shown when Daily recurrence is selected */}
                  {localFormData.recurrence_pattern ===
                    RecurrencePattern.DAILY && (
                    <div className="col-span-1">
                      <label
                        htmlFor="shift-end-date-input"
                        className="block text-sm font-medium !text-gray-900 mb-2"
                      >
                        {intl.formatMessage({
                          id: 'CLUB_RESOURCE.FORM.SHIFT_END_DATE',
                        })}
                      </label>
                      <input
                        id="shift-end-date-input"
                        name="shift_end_date"
                        type="date"
                        autoComplete="off"
                        className={clsx(
                          'input input-bordered w-full',
                          errors.shift_end_date &&
                            'border-red-500 focus:ring-red-500'
                        )}
                        value={localFormData.shift_end_date || ''}
                        onChange={handleInputChange}
                        disabled={
                          isReadOnly ||
                          (mode === 'edit' &&
                            localFormData.resource_type ===
                              ResourceType.PORT_WATCH_SHIFTS)
                        }
                        min={
                          localFormData.shift_start_date ||
                          new Date().toISOString().split('T')[0]
                        }
                        tabIndex={0}
                        onKeyDown={(e) => {
                          // Only allow navigation keys, prevent typing
                          if (
                            e.key === 'Tab' ||
                            e.key === 'Enter' ||
                            e.key === 'ArrowUp' ||
                            e.key === 'ArrowDown' ||
                            e.key === 'ArrowLeft' ||
                            e.key === 'ArrowRight' ||
                            e.key === 'Escape'
                          ) {
                            return; // Allow navigation keys only
                          }
                          e.preventDefault(); // Prevent all other keys including typing
                        }}
                        aria-invalid={!!errors.shift_end_date}
                        aria-describedby={
                          errors.shift_end_date
                            ? 'shift-end-date-error'
                            : undefined
                        }
                      />
                      {errors.shift_end_date && (
                        <p
                          id="shift-end-date-error"
                          className="text-red-500 text-sm mt-1"
                        >
                          {errors.shift_end_date}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Recurrence Pattern for PORT_WATCH_SHIFTS - Shown after timing configuration */}
                <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-6">
                  <div className="col-span-1">
                    <label
                      htmlFor="port-watch-recurrence-pattern-select"
                      className="block text-sm font-medium !text-gray-900 mb-2"
                    >
                      {intl.formatMessage({
                        id: 'CLUB_RESOURCE.FORM.RECURRENCE_PATTERN',
                      })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="port-watch-recurrence-pattern-select"
                      name="recurrence_pattern"
                      className={clsx(
                        'select select-bordered w-full',
                        errors.recurrence_pattern &&
                          'border-red-500 focus:ring-red-500'
                      )}
                      value={localFormData.recurrence_pattern || ''}
                      onChange={handleSelectChange}
                      disabled={
                        isReadOnly ||
                        (mode === 'edit' &&
                          localFormData.resource_type ===
                            ResourceType.PORT_WATCH_SHIFTS)
                      }
                      aria-invalid={!!errors.recurrence_pattern}
                      aria-describedby={
                        errors.recurrence_pattern
                          ? 'port-watch-recurrence-pattern-error'
                          : undefined
                      }
                    >
                      <option value="">
                        {intl.formatMessage({
                          id: 'CLUB_RESOURCE.FORM.RECURRENCE_PATTERN.SELECT',
                        })}
                      </option>
                      <option value={RecurrencePattern.SINGLE}>
                        {intl.formatMessage({
                          id: 'CLUB_RESOURCE.FORM.RECURRENCE_PATTERN.SINGLE',
                        })}
                      </option>
                      <option value={RecurrencePattern.DAILY}>
                        {intl.formatMessage({
                          id: 'CLUB_RESOURCE.FORM.RECURRENCE_PATTERN.DAILY',
                        })}
                      </option>
                      <option value={RecurrencePattern.SEASONAL}>
                        {intl.formatMessage({
                          id: 'CLUB_RESOURCE.FORM.RECURRENCE_PATTERN.SEASONAL',
                        })}
                      </option>
                    </select>
                    {errors.recurrence_pattern && (
                      <p
                        id="port-watch-recurrence-pattern-error"
                        className="text-red-500 text-sm mt-1"
                      >
                        {errors.recurrence_pattern}
                      </p>
                    )}
                  </div>
                </div>

                {/* Member Liability - Only for Port Watch Shifts */}
                <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-6">
                  <div className="col-span-1">
                    <label
                      htmlFor="member-liability-input"
                      className="block text-sm font-medium !text-gray-900 mb-2"
                    >
                      {intl.formatMessage({
                        id: 'CLUB_RESOURCE.FORM.MEMBER_LIABILITY',
                      })}
                    </label>
                    <NumberInput
                      id="member-liability-input"
                      name="port_watch_duty_liability"
                      autoComplete="off"
                      className={clsx(
                        'input input-bordered w-full',
                        errors.port_watch_duty_liability &&
                          'border-red-500 focus:ring-red-500'
                      )}
                      value={
                        localFormData.port_watch_duty_liability !== undefined
                          ? localFormData.port_watch_duty_liability
                          : ''
                      }
                      onChange={(e) =>
                        handleNumberInputChange(e, 'port_watch_duty_liability')
                      }
                      disabled={isReadOnly}
                      placeholder={intl.formatMessage({
                        id: 'CLUB_RESOURCE.FORM.MEMBER_LIABILITY.PLACEHOLDER',
                      })}
                      min="0"
                      aria-invalid={!!errors.port_watch_duty_liability}
                      aria-describedby={
                        errors.port_watch_duty_liability
                          ? 'member-liability-error'
                          : undefined
                      }
                    />
                    {errors.port_watch_duty_liability && (
                      <p
                        id="member-liability-error"
                        className="text-red-500 text-sm mt-1"
                      >
                        {errors.port_watch_duty_liability}
                      </p>
                    )}
                  </div>
                  <div className="col-span-1">
                    <label
                      htmlFor="buyout-amount-input"
                      className="block text-sm font-medium !text-gray-900 mb-2"
                    >
                      Buy-Out Amount
                    </label>
                    <NumberInput
                      id="buyout-amount-input"
                      name="port_watch_buyout_amount"
                      autoComplete="off"
                      className={clsx(
                        'input input-bordered w-full',
                        errors.port_watch_buyout_amount &&
                          'border-red-500 focus:ring-red-500'
                      )}
                      value={
                        localFormData.port_watch_buyout_amount !== undefined &&
                        localFormData.port_watch_buyout_amount !== null
                          ? localFormData.port_watch_buyout_amount
                          : ''
                      }
                      onChange={(e) =>
                        handleNumberInputChange(e, 'port_watch_buyout_amount')
                      }
                      disabled={isReadOnly}
                      placeholder="Amount per duty unit"
                      min="0"
                      aria-invalid={!!errors.port_watch_buyout_amount}
                      aria-describedby={
                        errors.port_watch_buyout_amount
                          ? 'buyout-amount-error'
                          : undefined
                      }
                    />
                    {errors.port_watch_buyout_amount && (
                      <p
                        id="buyout-amount-error"
                        className="text-red-500 text-sm mt-1"
                      >
                        {errors.port_watch_buyout_amount}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-3 text-sm text-gray-900">
                  <p>
                    <strong>
                      {intl.formatMessage({
                        id: 'CLUB_RESOURCE.PORT_WATCH.NOTE_LABEL',
                      })}
                    </strong>{' '}
                    {localFormData.recurrence_pattern ===
                    RecurrencePattern.DAILY
                      ? intl.formatMessage({
                          id: 'CLUB_RESOURCE.PORT_WATCH.NOTE_DAILY',
                        })
                      : localFormData.recurrence_pattern ===
                          RecurrencePattern.SINGLE
                        ? intl.formatMessage({
                            id: 'CLUB_RESOURCE.PORT_WATCH.NOTE_SINGLE',
                          })
                        : intl.formatMessage({
                            id: 'CLUB_RESOURCE.PORT_WATCH.NOTE_GENERIC',
                          })}
                  </p>
                </div>
              </div>
            )}

            {/* Sauna Configuration - Only for Sauna Booking, exclude when recurrence is Seasonal */}
            {localFormData.resource_type === ResourceType.SAUNA_BOOKING &&
              localFormData.recurrence_pattern !==
                RecurrencePattern.SEASONAL && (
                <div className="mt-6 p-4 rounded-lg border border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900 mb-4">
                    {intl.formatMessage({
                      id: 'CLUB_RESOURCE.SECTION.SAUNA.TITLE',
                    })}
                  </h2>

                  {/* Weekly Windows */}
                  <div className="mb-6">
                    <h3 className="text-md font-medium text-gray-800 mb-3">
                      {intl.formatMessage({
                        id: 'CLUB_RESOURCE.SECTION.SAUNA.WEEKLY_WINDOWS_TITLE',
                      })}{' '}
                      {weeklyWindowsRequired && (
                        <span className="text-red-500">*</span>
                      )}
                    </h3>
                    <div className="space-y-4">
                      {(
                        [
                          'mon',
                          'tue',
                          'wed',
                          'thu',
                          'fri',
                          'sat',
                          'sun',
                        ] as const
                      ).map((day) => {
                        const firstInputId = `weekly-window-${day}-0-start`;
                        const hasWindows =
                          weeklyWindows[day] && weeklyWindows[day].length > 0;
                        return (
                          <div
                            key={day}
                            className="border border-gray-200 rounded-lg p-3"
                          >
                            <div className="flex items-center justify-between mb-2">
                              {hasWindows ? (
                                <label
                                  htmlFor={firstInputId}
                                  className="text-sm font-medium text-gray-700 capitalize"
                                >
                                  {intl.formatMessage({
                                    id:
                                      day === 'mon'
                                        ? 'POI.OPENING_HOURS.MONDAY'
                                        : day === 'tue'
                                          ? 'POI.OPENING_HOURS.TUESDAY'
                                          : day === 'wed'
                                            ? 'POI.OPENING_HOURS.WEDNESDAY'
                                            : day === 'thu'
                                              ? 'POI.OPENING_HOURS.THURSDAY'
                                              : day === 'fri'
                                                ? 'POI.OPENING_HOURS.FRIDAY'
                                                : day === 'sat'
                                                  ? 'POI.OPENING_HOURS.SATURDAY'
                                                  : 'POI.OPENING_HOURS.SUNDAY',
                                  })}
                                </label>
                              ) : (
                                <span className="text-sm font-medium text-gray-700 capitalize">
                                  {intl.formatMessage({
                                    id:
                                      day === 'mon'
                                        ? 'POI.OPENING_HOURS.MONDAY'
                                        : day === 'tue'
                                          ? 'POI.OPENING_HOURS.TUESDAY'
                                          : day === 'wed'
                                            ? 'POI.OPENING_HOURS.WEDNESDAY'
                                            : day === 'thu'
                                              ? 'POI.OPENING_HOURS.THURSDAY'
                                              : day === 'fri'
                                                ? 'POI.OPENING_HOURS.FRIDAY'
                                                : day === 'sat'
                                                  ? 'POI.OPENING_HOURS.SATURDAY'
                                                  : 'POI.OPENING_HOURS.SUNDAY',
                                  })}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => handleAddWeeklyWindow(day)}
                                disabled={isReadOnly}
                                className="btn btn-sm btn-outline btn-primary contrast:text-blue-200"
                              >
                                {intl.formatMessage({
                                  id: 'CLUB_RESOURCE.SECTION.SAUNA.ADD_TIME_WINDOW',
                                })}
                              </button>
                            </div>
                            <div className="space-y-2">
                              {(weeklyWindows[day] || []).map((slot, index) => (
                                <div
                                  key={index}
                                  className="flex items-center gap-2"
                                >
                                  <input
                                    id={index === 0 ? firstInputId : undefined}
                                    type="time"
                                    autoComplete="off"
                                    value={slot.start_time}
                                    onChange={(e) =>
                                      handleUpdateWeeklyWindow(day, index, {
                                        ...slot,
                                        start_time: e.target.value,
                                      })
                                    }
                                    disabled={isReadOnly}
                                    className="input input-bordered input-sm flex-1"
                                    aria-label={`${intl.formatMessage({
                                      id:
                                        day === 'mon'
                                          ? 'POI.OPENING_HOURS.MONDAY'
                                          : day === 'tue'
                                            ? 'POI.OPENING_HOURS.TUESDAY'
                                            : day === 'wed'
                                              ? 'POI.OPENING_HOURS.WEDNESDAY'
                                              : day === 'thu'
                                                ? 'POI.OPENING_HOURS.THURSDAY'
                                                : day === 'fri'
                                                  ? 'POI.OPENING_HOURS.FRIDAY'
                                                  : day === 'sat'
                                                    ? 'POI.OPENING_HOURS.SATURDAY'
                                                    : 'POI.OPENING_HOURS.SUNDAY',
                                    })} time window ${index + 1} start time`}
                                  />
                                  <span className="text-gray-500">to</span>
                                  <input
                                    type="time"
                                    autoComplete="off"
                                    value={slot.end_time}
                                    onChange={() => {}}
                                    readOnly
                                    disabled
                                    className="input input-bordered input-sm flex-1"
                                    aria-label={`${intl.formatMessage({
                                      id:
                                        day === 'mon'
                                          ? 'POI.OPENING_HOURS.MONDAY'
                                          : day === 'tue'
                                            ? 'POI.OPENING_HOURS.TUESDAY'
                                            : day === 'wed'
                                              ? 'POI.OPENING_HOURS.WEDNESDAY'
                                              : day === 'thu'
                                                ? 'POI.OPENING_HOURS.THURSDAY'
                                                : day === 'fri'
                                                  ? 'POI.OPENING_HOURS.FRIDAY'
                                                  : day === 'sat'
                                                    ? 'POI.OPENING_HOURS.SATURDAY'
                                                    : 'POI.OPENING_HOURS.SUNDAY',
                                    })} time window ${index + 1} end time`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveWeeklyWindow(day, index)
                                    }
                                    disabled={isReadOnly}
                                    className="btn btn-sm btn-ghost text-red-500 hover:bg-red-500 hover:text-white"
                                    aria-label={`Remove ${intl.formatMessage({
                                      id:
                                        day === 'mon'
                                          ? 'POI.OPENING_HOURS.MONDAY'
                                          : day === 'tue'
                                            ? 'POI.OPENING_HOURS.TUESDAY'
                                            : day === 'wed'
                                              ? 'POI.OPENING_HOURS.WEDNESDAY'
                                              : day === 'thu'
                                                ? 'POI.OPENING_HOURS.THURSDAY'
                                                : day === 'fri'
                                                  ? 'POI.OPENING_HOURS.FRIDAY'
                                                  : day === 'sat'
                                                    ? 'POI.OPENING_HOURS.SATURDAY'
                                                    : 'POI.OPENING_HOURS.SUNDAY',
                                    })} time window ${index + 1}`}
                                  >
                                    <KeenIcon
                                      type="solid"
                                      icon="trash"
                                      className="text-base"
                                    />
                                  </button>
                                </div>
                              ))}
                              {(!weeklyWindows[day] ||
                                weeklyWindows[day]?.length === 0) && (
                                <p className="text-sm text-gray-500 italic">
                                  {intl.formatMessage({
                                    id: 'CLUB_RESOURCE.SECTION.SAUNA.WEEKLY_WINDOWS_EMPTY',
                                  })}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {errors.weekly_windows && (
                    <p className="text-red-500 text-sm mt-2">
                      {errors.weekly_windows}
                    </p>
                  )}

                  {/* Special Date Windows */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-md font-medium text-gray-800">
                        {intl.formatMessage({
                          id: 'CLUB_RESOURCE.SECTION.SAUNA.SPECIAL_WINDOWS_TITLE',
                        })}
                      </h3>
                      <button
                        type="button"
                        onClick={handleAddSpecialDateWindow}
                        disabled={isReadOnly}
                        className="btn btn-sm btn-outline btn-primary contrast:text-blue-200"
                      >
                        {intl.formatMessage({
                          id: 'CLUB_RESOURCE.SECTION.SAUNA.ADD_SPECIAL_DATE',
                        })}
                      </button>
                    </div>
                    <div className="space-y-3">
                      {specialDateWindows.map((specialDate, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-lg p-3"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <input
                              type="date"
                              autoComplete="off"
                              min={todayStr}
                              max={saunaMaxDate}
                              value={specialDate.date}
                              onChange={(e) =>
                                handleUpdateSpecialDateWindow(index, {
                                  ...specialDate,
                                  date: e.target.value,
                                })
                              }
                              disabled={isReadOnly}
                              className="input input-bordered input-sm"
                              aria-label={`Special date ${index + 1}`}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                handleRemoveSpecialDateWindow(index)
                              }
                              disabled={isReadOnly}
                              className="btn btn-sm btn-ghost text-red-500 hover:bg-red-500 hover:text-white"
                              aria-label={`Remove special date ${index + 1}`}
                            >
                              <KeenIcon
                                type="solid"
                                icon="trash"
                                className="text-base"
                              />
                            </button>
                          </div>
                          <div className="space-y-2">
                            {specialDate.windows.map((slot, slotIndex) => (
                              <div
                                key={slotIndex}
                                className="flex items-center gap-2 ml-4"
                              >
                                <input
                                  type="time"
                                  autoComplete="off"
                                  value={slot.start_time}
                                  onChange={(e) =>
                                    handleUpdateSpecialDateWindow(index, {
                                      ...specialDate,
                                      windows: specialDate.windows.map(
                                        (w, j) =>
                                          j === slotIndex
                                            ? {
                                                ...w,
                                                start_time: e.target.value,
                                              }
                                            : w
                                      ),
                                    })
                                  }
                                  disabled={isReadOnly}
                                  className="input input-bordered input-sm flex-1"
                                  aria-label={`Special date ${index + 1} time window ${slotIndex + 1} start time`}
                                />
                                <span className="text-gray-500">to</span>
                                <input
                                  type="time"
                                  autoComplete="off"
                                  value={slot.end_time}
                                  onChange={() => {}}
                                  readOnly
                                  disabled
                                  className="input input-bordered input-sm flex-1"
                                  aria-label={`Special date ${index + 1} time window ${slotIndex + 1} end time`}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = {
                                      ...specialDate,
                                      windows: specialDate.windows.filter(
                                        (_, j) => j !== slotIndex
                                      ),
                                    };
                                    handleUpdateSpecialDateWindow(
                                      index,
                                      updated
                                    );
                                  }}
                                  disabled={isReadOnly}
                                  className="btn btn-sm btn-ghost text-red-500 hover:bg-red-500 hover:text-white"
                                  aria-label={`Remove special date ${index + 1} time window ${slotIndex + 1}`}
                                >
                                  <KeenIcon
                                    type="solid"
                                    icon="trash"
                                    className="text-base"
                                  />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() =>
                                handleUpdateSpecialDateWindow(index, {
                                  ...specialDate,
                                  windows: [
                                    ...specialDate.windows,
                                    {
                                      start_time: '09:00',
                                      end_time:
                                        computeEndTime('09:00') || '10:00',
                                    },
                                  ],
                                })
                              }
                              disabled={isReadOnly}
                              className="btn btn-sm btn-outline btn-primary contrast:text-blue-200 ml-4"
                            >
                              Add Time Window
                            </button>
                          </div>
                        </div>
                      ))}
                      {specialDateWindows.length === 0 && (
                        <p className="text-sm text-gray-500 italic">
                          {intl.formatMessage({
                            id: 'CLUB_RESOURCE.SECTION.SAUNA.SPECIAL_WINDOWS_EMPTY',
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                  {errors.special_date_windows && (
                    <p className="text-red-500 text-sm mt-2">
                      {errors.special_date_windows}
                    </p>
                  )}

                  {/* Closed Dates */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-md font-medium text-gray-800">
                        {intl.formatMessage({
                          id: 'CLUB_RESOURCE.SECTION.SAUNA.CLOSED_DATES_TITLE',
                        })}
                      </h3>
                      <button
                        type="button"
                        onClick={handleAddClosedDate}
                        disabled={isReadOnly}
                        className="btn btn-sm btn-outline btn-primary contrast:text-blue-200"
                      >
                        {intl.formatMessage({
                          id: 'CLUB_RESOURCE.SECTION.SAUNA.ADD_CLOSED_DATE',
                        })}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {closedDates.map((date, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="date"
                            autoComplete="off"
                            min={todayStr}
                            max={saunaMaxDate}
                            value={date}
                            onChange={(e) =>
                              handleUpdateClosedDate(index, e.target.value)
                            }
                            disabled={isReadOnly}
                            className="input input-bordered input-sm flex-1"
                            aria-label={`Closed date ${index + 1}`}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveClosedDate(index)}
                            disabled={isReadOnly}
                            className="btn btn-sm btn-ghost text-red-500 hover:bg-red-500 hover:text-white"
                            aria-label={`Remove closed date ${index + 1}`}
                          >
                            <KeenIcon
                              type="solid"
                              icon="trash"
                              className="text-base"
                            />
                          </button>
                        </div>
                      ))}
                      {closedDates.length === 0 && (
                        <p className="text-sm text-gray-500 italic col-span-2">
                          {intl.formatMessage({
                            id: 'CLUB_RESOURCE.SECTION.SAUNA.CLOSED_DATES_EMPTY',
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                  {errors.closed_dates && (
                    <p className="text-red-500 text-sm mt-2">
                      {errors.closed_dates}
                    </p>
                  )}

                  <div className="mt-3 text-sm text-gray-600">
                    <p>
                      <strong>
                        {intl.formatMessage({
                          id: 'CLUB_RESOURCE.SECTION.SAUNA.NOTE_LABEL',
                        })}
                      </strong>{' '}
                      {intl.formatMessage({
                        id: 'CLUB_RESOURCE.SECTION.SAUNA.NOTE_TEXT',
                      })}
                    </p>
                  </div>
                </div>
              )}

            {/* Seasonal Availability - Conditional */}
            {localFormData.recurrence_pattern ===
              RecurrencePattern.SEASONAL && (
              <SeasonalDatePicker
                seasonalDates={
                  localFormData.seasonal_dates || {
                    spring: { start_date: '', end_date: '' },
                    summer: { start_date: '', end_date: '' },
                    autumn: { start_date: '', end_date: '' },
                    winter: { start_date: '', end_date: '' },
                  }
                }
                onChange={handleSeasonalDateChange}
                disabled={isReadOnly}
                errors={{
                  spring_start:
                    errors.spring_start || errors.spring_start_date || '',
                  spring_end: errors.spring_end || errors.spring_end_date || '',
                  summer_start:
                    errors.summer_start || errors.summer_start_date || '',
                  summer_end: errors.summer_end || errors.summer_end_date || '',
                  autumn_start:
                    errors.autumn_start || errors.autumn_start_date || '',
                  autumn_end: errors.autumn_end || errors.autumn_end_date || '',
                  winter_start:
                    errors.winter_start || errors.winter_start_date || '',
                  winter_end: errors.winter_end || errors.winter_end_date || '',
                }}
              />
            )}
          </form>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 mt-6 pt-6 border-t">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {mode === 'view' ? 'Close' : 'Cancel'}
          </button>
          {!isReadOnly && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={16} className="mr-2" />
                  {mode === 'create' ? 'Creating...' : 'Updating...'}
                </>
              ) : mode === 'create' ? (
                'Create'
              ) : (
                'Update'
              )}
            </button>
          )}
        </div>
        <ClubResourcePreviewModal
          open={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          formData={localFormData}
          source_language={currentLanguage.code}
          onTranslationsGenerated={(newTranslations) => {
            setTranslations(newTranslations);
          }}
        />
      </div>
    </Modal>
  );
};

export { ClubResourceFormModal };
